import { useState, type ReactNode } from 'react';
import { KeyRound, LogOut, RefreshCw } from 'lucide-react';
import { send } from '../../api';
import { Button } from '../ui/button';
import { ConfirmDialog, Sheet } from '../ui/side-sheet';
import type { MenuItem } from '../ui/menu';
import { EnvironmentBadge, IntegrationCard, Logo, type CardMessage, type Fact } from './IntegrationCard';
import { booksFreshness, sandboxLine, sourceHealth, sourceRunLine, syncLine, vendorName, withFreshness, type IntegrationsState,
  type SourceName } from './model';
import type { SourceSyncSummary } from '../../../../src/application/sources/sync';

export interface CredentialField { key: string; label: string; secret?: boolean; placeholder?: string; hint?: string }
export interface SourceConfig {
  name: SourceName; logo: string; logoFill?: boolean; role: string; reassurance: string; purpose: string;
  /** The card's plain sentence: what Alder uses this for here. */
  summary: string;
  fields: CredentialField[];
}
const message = (e: unknown) => e instanceof Error ? e.message : 'This step could not be completed.';

/**
 * A read-only books connector (Rillet or Sequence). Credentials open in a sheet; "Test & save" makes one read-only call
 * with exactly the entered values and saves them encrypted only if it succeeds, then syncs.
 */
export function SourceCard({ config, state, onChanged, onSyncNow, syncing, children, details }: {
  config: SourceConfig; state: IntegrationsState; onChanged: () => Promise<void>; onSyncNow: () => void; syncing: boolean;
  /** Visible extra rows (Sequence's instant-updates note). */
  children?: ReactNode;
  /** Extra technical content for the collapsed Details section (Sequence's webhook setup). */
  details?: ReactNode;
}) {
  const status = state.connectors[config.name];
  const label = vendorName[config.name];
  const [open, setOpen] = useState(false); const [confirming, setConfirming] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [environment, setEnvironment] = useState(status.environment ?? 'sandbox');
  const [busy, setBusy] = useState(false); const [formError, setFormError] = useState('');
  const [result, setResult] = useState<CardMessage>(null);
  const saved = status.source === 'saved'; const present = status.source === 'saved' || status.source === 'env';
  const freshness = present ? booksFreshness(state) : null;
  const health = withFreshness(sourceHealth(config.name, state), freshness);
  const badge = present && status.environment === 'sandbox'
    ? { label: 'Sandbox copy', text: sandboxLine(label, Boolean(state.shadowMode)) } : null;

  function openSheet() { setValues({}); setEnvironment(status.environment ?? 'sandbox'); setFormError(''); setOpen(true); }
  async function save() {
    setBusy(true); setFormError('');
    try {
      const body = { ...Object.fromEntries(config.fields.map(f => [f.key, values[f.key] ?? ''])), environment };
      const response = await send<{ sync: SourceSyncSummary }>(`/app-api/integrations/${config.name}`, 'PUT', body);
      setOpen(false); setValues({});
      setResult({ kind: 'success', text: `${label} connected. ${syncLine(response.sync)}.` });
      await onChanged();
    } catch (e) { setFormError(message(e)); } finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true);
    try {
      await send(`/app-api/integrations/${config.name}`, 'DELETE');
      setConfirming(false); setResult({ kind: 'success', text: `${label} disconnected. Its saved values were deleted.` });
      await onChanged();
    } catch (e) { setConfirming(false); setResult({ kind: 'error', text: message(e) }); } finally { setBusy(false); }
  }

  const facts: Fact[] = [];
  if (present || status.source === 'error') {
    facts.push({ label: 'Environment', value: <EnvironmentBadge environment={status.environment}/> });
    for (const field of config.fields.filter(f => f.secret || f.key === 'clientId')) {
      facts.push({ label: field.label, value: <code className="masked">{status.fields[field.key] ?? 'Not set'}</code> });
    }
    facts.push({ label: 'Credentials', value: status.source === 'env' ? 'From host settings'
      : status.savedAt ? `Saved ${new Date(status.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : 'Saved' });
    const run = sourceRunLine(config.name, state.last);
    if (run) facts.push({ label: 'Last read', value: run });
  }
  const canEdit = state.canSave;
  const primaryLabel = status.source === 'none' ? `Connect ${label}` : status.source === 'error' ? 'Re-enter credentials'
    : 'Update credentials';
  const menu: MenuItem[] = [];
  if (present) menu.push({ label: 'Sync now', icon: <RefreshCw size={15} aria-hidden/>, onSelect: onSyncNow,
    disabled: syncing || !state.live });
  if (present && canEdit) menu.push({ label: 'Replace credentials', icon: <KeyRound size={15} aria-hidden/>, onSelect: openSheet });
  if (saved) menu.push({ label: 'Disconnect', icon: <LogOut size={15} aria-hidden/>, danger: true,
    onSelect: () => setConfirming(true) });
  // Connected cards keep their actions in the menu; the page header has the one visible Sync now.
  const primary = !state.live || (present && health.tone === 'connected') ? null
    : <Button size="sm" disabled={!canEdit} onClick={openSheet}>{primaryLabel}</Button>;

  return <>
    <IntegrationCard id={config.name} logo={config.logo} logoFill={config.logoFill} name={label} role={config.role}
      health={health} summary={config.summary} freshness={freshness} badge={badge} facts={facts} details={details}
      reassurance={config.reassurance} message={result} primary={primary} menu={menu}>
      {children}</IntegrationCard>
    <Sheet open={open} onClose={() => setOpen(false)} title={status.source === 'none' ? `Connect ${label}` : `${label} credentials`}
      description={config.purpose} icon={<Logo src={config.logo} fill={config.logoFill}/>}
      footer={<>
        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        <Button disabled={busy || !canEdit} onClick={() => void save()}>{busy ? 'Testing…' : 'Test & save'}</Button>
      </>}>
      <form className="sheet-form" onSubmit={event => { event.preventDefault(); void save(); }}>
        {status.source === 'env' && <p className="sheet-note">Running on host settings now. Once you save here, these values
          replace the host settings for {label}.</p>}
        {status.source === 'error' && <p className="sheet-note warn">The saved values cannot be read. Enter every field again.</p>}
        {config.fields.map(field => <label key={field.key}><span>{field.label}</span>
          <input type={field.secret ? 'password' : 'text'} autoComplete="off" spellCheck={false} value={values[field.key] ?? ''}
            onChange={event => setValues({ ...values, [field.key]: event.target.value })}
            placeholder={status.fields[field.key] ?? field.placeholder ?? ''}/>
          {field.hint && <small>{field.hint}</small>}
        </label>)}
        <fieldset className="segmented"><legend>Environment</legend>
          {(['sandbox', 'production'] as const).map(value => <label key={value}>
            <input type="radio" name={`${config.name}-environment`} value={value} checked={environment === value}
              onChange={() => setEnvironment(value)}/><span>{value === 'sandbox' ? 'Sandbox' : 'Production'}</span></label>)}
        </fieldset>
        {saved && <p className="sheet-hint">Leave a field blank to keep its saved value.</p>}
        <p className="sheet-assurance">{config.reassurance} Alder tests these values with one read-only request and saves
          them encrypted only if it succeeds. Saved keys show only their last four characters.</p>
        {formError && <p className="integration-error" role="alert">{formError}</p>}
        <button type="submit" hidden/>
      </form>
    </Sheet>
    <ConfirmDialog open={confirming} title={`Disconnect ${label}?`} busy={busy} confirmLabel={`Disconnect ${label}`}
      body={`Alder stops reading ${label} and deletes the saved credentials. Invoices already synced stay in Alder.`}
      onConfirm={() => void disconnect()} onClose={() => setConfirming(false)}/>
  </>;
}
